/**
 * GoogleTargetingPanel
 * Platform-specific targeting UI for Google Ads
 * Includes audience segments, keywords (for Search), and remarketing lists
 */

import React, { useState, useEffect } from 'react';
import { Users, Target, Key, Search as SearchIcon, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import InterestSearch from './InterestSearch';
import LocationSearch from './LocationSearch';
import AudienceEstimate from './AudienceEstimate';
import AITargetingHelper from './AITargetingHelper';

export default function GoogleTargetingPanel({
    brandId,
    userId,
    brand, // Full brand object for AI suggestions
    targeting = {},
    onChange,
    isConnected,
    accountId = null,
    campaignType = 'SEARCH', // 'SEARCH', 'DISPLAY', 'VIDEO', 'SHOPPING'
    funnelStage = 'TOFU'
}) {
    const [audienceSegments, setAudienceSegments] = useState({ affinity: [], inMarket: [] });
    const [userLists, setUserLists] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        demographics: true,
        locations: true,
        keywords: campaignType === 'SEARCH',
        audiences: campaignType !== 'SEARCH'
    });

    useEffect(() => {
        if (isConnected && accountId) {
            loadAudienceOptions();
        }
    }, [isConnected, accountId]);

    const loadAudienceOptions = async () => {
        setLoadingOptions(true);
        try {
            const response = await fetch(`/api/targeting/google/options?brandId=${brandId}&userId=${userId}`);
            if (response.ok) {
                const data = await response.json();
                setAudienceSegments(data.segments || { affinity: [], inMarket: [] });
                setUserLists(data.userLists || []);
            }
        } catch (err) {
            console.error('Error loading Google targeting options:', err);
        } finally {
            setLoadingOptions(false);
        }
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
    };

    const updateTargeting = (key, value) => {
        onChange({ ...targeting, [key]: value });
    };

    const updateDemographics = (field, value) => {
        onChange({
            ...targeting,
            demographics: { ...targeting.demographics, [field]: value }
        });
    };

    // Keyword management for Search campaigns
    const [keywordInput, setKeywordInput] = useState('');

    const addKeyword = () => {
        if (!keywordInput.trim()) return;
        const current = targeting.detailedTargeting?.google?.keywords || [];
        const newKeyword = {
            text: keywordInput.trim(),
            matchType: 'broad',
            id: `kw_${Date.now()}`
        };
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            google: {
                ...targeting.detailedTargeting?.google,
                keywords: [...current, newKeyword]
            }
        });
        setKeywordInput('');
    };

    const removeKeyword = (id) => {
        const current = targeting.detailedTargeting?.google?.keywords || [];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            google: {
                ...targeting.detailedTargeting?.google,
                keywords: current.filter(k => k.id !== id)
            }
        });
    };

    const handleLocationSelect = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', { ...targeting.locations, included: [...current, loc] });
    };

    const handleLocationRemove = (loc) => {
        const current = targeting.locations?.included || [];
        updateTargeting('locations', {
            ...targeting.locations,
            included: current.filter(l => l.id !== loc.id)
        });
    };

    const handleAudienceSelect = (segmentOrSegments, type) => {
        const current = targeting.detailedTargeting?.google?.audienceSegments?.[type] || [];
        const items = Array.isArray(segmentOrSegments) ? segmentOrSegments : [segmentOrSegments];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            google: {
                ...targeting.detailedTargeting?.google,
                audienceSegments: {
                    ...targeting.detailedTargeting?.google?.audienceSegments,
                    [type]: [...current, ...items]
                }
            }
        });
    };

    const handleAudienceRemove = (segment, type) => {
        const current = targeting.detailedTargeting?.google?.audienceSegments?.[type] || [];
        updateTargeting('detailedTargeting', {
            ...targeting.detailedTargeting,
            google: {
                ...targeting.detailedTargeting?.google,
                audienceSegments: {
                    ...targeting.detailedTargeting?.google?.audienceSegments,
                    [type]: current.filter(s => s.id !== segment.id)
                }
            }
        });
    };

    const keywords = targeting.detailedTargeting?.google?.keywords || [];

    return (
        <div className="google-targeting-panel">
            {/* Demographics */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('demographics')}>
                    <Users size={16} />
                    <span className="section-title">Demographics</span>
                    {expandedSections.demographics ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.demographics && (
                    <div className="section-content">
                        <div className="form-group">
                            <label>Age Range (18+)</label>
                            <div className="age-range">
                                <select value={targeting.demographics?.ageMin || 18} onChange={(e) => updateDemographics('ageMin', parseInt(e.target.value))}>
                                    {[18, 25, 35, 45, 55, 65].map(age => (<option key={age} value={age}>{age}</option>))}
                                </select>
                                <span>to</span>
                                <select value={targeting.demographics?.ageMax || 65} onChange={(e) => updateDemographics('ageMax', parseInt(e.target.value))}>
                                    {[24, 34, 44, 54, 64, 65].map(age => (<option key={age} value={age}>{age}{age === 65 ? '+' : ''}</option>))}
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Locations */}
            <div className="targeting-section">
                <button type="button" className="section-header" onClick={() => toggleSection('locations')}>
                    <Target size={16} />
                    <span className="section-title">Locations</span>
                    {(targeting.locations?.included?.length || 0) > 0 && (
                        <span className="section-count">{targeting.locations.included.length}</span>
                    )}
                    {expandedSections.locations ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                </button>
                {expandedSections.locations && (
                    <div className="section-content">
                        <LocationSearch
                            platform="google"
                            brandId={brandId}
                            userId={userId}
                            selectedLocations={targeting.locations?.included || []}
                            excludedLocations={targeting.locations?.excluded || []}
                            onSelect={handleLocationSelect}
                            onRemove={handleLocationRemove}
                            showExclusions={false}
                            placeholder="Search locations..."
                        />
                    </div>
                )}
            </div>

            {/* Keywords (Search campaigns only) */}
            {campaignType === 'SEARCH' && (
                <div className="targeting-section">
                    <button type="button" className="section-header" onClick={() => toggleSection('keywords')}>
                        <Key size={16} />
                        <span className="section-title">Keywords</span>
                        {keywords.length > 0 && <span className="section-count">{keywords.length}</span>}
                        {expandedSections.keywords ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {expandedSections.keywords && (
                        <div className="section-content">
                            <div className="keyword-input-wrapper">
                                <input
                                    type="text"
                                    value={keywordInput}
                                    onChange={(e) => setKeywordInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                                    placeholder="Add keyword and press Enter..."
                                    className="keyword-input"
                                />
                                <button type="button" onClick={addKeyword} className="add-keyword-btn">Add</button>
                            </div>
                            {keywords.length > 0 && (
                                <div className="keyword-list">
                                    {keywords.map(kw => (
                                        <span key={kw.id} className="keyword-tag">
                                            <span>{kw.text}</span>
                                            <select
                                                value={kw.matchType}
                                                onChange={(e) => {
                                                    const updated = keywords.map(k => k.id === kw.id ? { ...k, matchType: e.target.value } : k);
                                                    updateTargeting('detailedTargeting', {
                                                        ...targeting.detailedTargeting,
                                                        google: { ...targeting.detailedTargeting?.google, keywords: updated }
                                                    });
                                                }}
                                                className="match-type-select"
                                            >
                                                <option value="broad">Broad</option>
                                                <option value="phrase">Phrase</option>
                                                <option value="exact">Exact</option>
                                            </select>
                                            <button type="button" onClick={() => removeKeyword(kw.id)} className="remove-btn">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Audience Segments (Display/Video) */}
            {(campaignType === 'DISPLAY' || campaignType === 'VIDEO') && (
                <div className="targeting-section">
                    <button type="button" className="section-header" onClick={() => toggleSection('audiences')}>
                        <Users size={16} />
                        <span className="section-title">Audience Segments</span>
                        {expandedSections.audiences ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    {expandedSections.audiences && (
                        <div className="section-content">
                            <AITargetingHelper
                                platform="google"
                                type="affinity"
                                brandId={brandId}
                                userId={userId}
                                brand={brand}
                                selectedItems={targeting.detailedTargeting?.google?.audienceSegments?.affinity || []}
                                onSelect={(item) => handleAudienceSelect(item, 'affinity')}
                                funnelStage={funnelStage}
                                disabled={!isConnected}
                            />
                            <InterestSearch
                                platform="google"
                                type="affinity"
                                brandId={brandId}
                                userId={userId}
                                selectedItems={targeting.detailedTargeting?.google?.audienceSegments?.affinity || []}
                                onSelect={(item) => handleAudienceSelect(item, 'affinity')}
                                onRemove={(item) => handleAudienceRemove(item, 'affinity')}
                                placeholder="Search affinity audiences..."
                            />
                            <div style={{ marginTop: '1rem' }}>
                                <AITargetingHelper
                                    platform="google"
                                    type="inMarket"
                                    brandId={brandId}
                                    userId={userId}
                                    brand={brand}
                                    selectedItems={targeting.detailedTargeting?.google?.audienceSegments?.inMarket || []}
                                    onSelect={(item) => handleAudienceSelect(item, 'inMarket')}
                                    funnelStage={funnelStage}
                                    disabled={!isConnected}
                                />
                                <InterestSearch
                                    platform="google"
                                    type="in_market"
                                    brandId={brandId}
                                    userId={userId}
                                    selectedItems={targeting.detailedTargeting?.google?.audienceSegments?.inMarket || []}
                                    onSelect={(item) => handleAudienceSelect(item, 'inMarket')}
                                    onRemove={(item) => handleAudienceRemove(item, 'inMarket')}
                                    placeholder="Search in-market audiences..."
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Audience Estimate */}
            <div className="estimate-section">
                <AudienceEstimate
                    platform="google"
                    brandId={brandId}
                    userId={userId}
                    targeting={targeting}
                    campaignType={campaignType}
                />
            </div>

            <style>{`
                .google-targeting-panel { display: flex; flex-direction: column; gap: 0.75rem; }
                .targeting-section { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 8px; }
                .section-header { display: flex; align-items: center; gap: 0.5rem; width: 100%; padding: 0.75rem 1rem; background: var(--color-bg-primary); border: none; text-align: left; cursor: pointer; color: var(--color-text-primary); }
                .section-title { flex: 1; font-size: 0.875rem; font-weight: 500; color: var(--color-text-primary); }
                .section-count { padding: 0.125rem 0.5rem; background: var(--color-primary); color: var(--color-text-inverted); border-radius: 10px; font-size: 0.75rem; }
                .section-content { padding: 1rem; border-top: 1px solid var(--color-border); }
                .form-group { margin-bottom: 1rem; }
                .form-group:last-child { margin-bottom: 0; }
                .form-group label { display: block; font-size: 0.8125rem; font-weight: 500; color: var(--color-text-tertiary); margin-bottom: 0.5rem; }
                .age-range { display: flex; align-items: center; gap: 0.5rem; }
                .age-range select { padding: 0.5rem; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 4px; font-size: 0.875rem; color: var(--color-text-primary); }
                .keyword-input-wrapper { display: flex; gap: 0.5rem; margin-bottom: 0.75rem; }
                .keyword-input { flex: 1; padding: 0.5rem; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 4px; font-size: 0.875rem; color: var(--color-text-primary); }
                .add-keyword-btn { padding: 0.5rem 1rem; background: var(--color-primary); color: var(--color-text-inverted); border: none; border-radius: 4px; cursor: pointer; }
                .keyword-list { display: flex; flex-wrap: wrap; gap: 0.5rem; }
                .keyword-tag { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.25rem 0.5rem; background: var(--color-bg-tertiary); color: var(--color-text-primary); border-radius: 4px; font-size: 0.8125rem; }
                .match-type-select { padding: 0.125rem 0.25rem; background: var(--color-bg-primary); border: 1px solid var(--color-border); border-radius: 3px; font-size: 0.75rem; color: var(--color-text-primary); }
                .remove-btn { padding: 0 0.25rem; background: none; border: none; color: var(--color-text-tertiary); cursor: pointer; font-size: 1rem; }
                .estimate-section { margin-top: 0.5rem; }
            `}</style>
        </div>
    );
}
